import ApplicationController from "application_controller.js";
import moment from "moment";
import * as easepick from "@gothassos/easepick-bundle";
window.easepick = easepick; //* fixes issues loading plugins

export default class extends ApplicationController {
  debug = false;

  static values = {
    locale: { type: String, default: "en-US" },
    css: { type: String, default: "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css" },
    availabilityUrl: String,
    minNights: { type: Number, default: 1 },
  };
  static targets = ["calendar", "calendarContainer", "starts", "ends", "duration"];

  connect() {
    this.log_function("connect");

    this.instance = new easepick.create({
      lang: this.currentLocale,
      element: this.calendarTarget,
      inline: true,
      firstDay: 1,
      translate: false,
      plugins: [
        "RangePlugin",
        "AmpPlugin",
        "LockPlugin",
      ],
      RangePlugin: {
        tooltipNumber(num) {
          return num - 1;
        },
        locale: this.nightsText,
      },
      AmpPlugin: {
        dropdown: {
          months: true,
          years: true,
          minYear: new Date().getFullYear(),
          maxYear: new Date().getFullYear() + 1,
        },
        resetButton: false,
        weekNumbers: false,
        darkMode: false,
      },
      LockPlugin: {
        //* minDays counts days, so a stay of N nights spans N + 1 days.
        minDays: this.minNightsValue + 1,
        maxDays: 365,
        selectForward: false,
        selectBackward: false,
        minDate: moment(),
        inseparable: true,
      },
      css: [this.cssValue],
      setup: (picker) => {
        picker.on("preselect", (event) => {
          const custom_event = new CustomEvent("easepick-preselect", event);
          window.dispatchEvent(custom_event);
          return this._handleSelect(event);
        });
        picker.on("select", (event) => {
          const custom_event = new CustomEvent("easepick-select", event);
          window.dispatchEvent(custom_event);
          return this._handleSelect(event);
        });
        picker.on("view", (event) => {
          this._markRestartDay(event);
          this._markStayEdges(event);
        });
        //* Registered before easepick's own click handler, so it runs first.
        picker.on("click", (event) => this._restartSelection(event));
        //* Registered after the RangePlugin hover handler, so it runs last.
        picker.on("mouseenter", (event) => this._hideRestartRange(event), true);
      },
    });

    if (this.availabilityUrlValue) this._lockBookedDates();
  }

  //* Locks the booked nights read from the availability endpoint. A stay is
  //* [arrival, departure) and the departure day stays free for the next
  //* arrival. Once an arrival is picked, the first booked day after it can
  //* still be the departure (the easepick hotel booking recipe).
  async _lockBookedDates() {
    this.log_function("_lockBookedDates");

    let booked;
    try {
      const response = await fetch(this.availabilityUrlValue, { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      booked = (await response.json()).booked;
    } catch {
      return;
    }
    if (!this.instance || !booked.length) return;

    const stays = booked.map(([arrival, departure]) => [
      new easepick.DateTime(arrival, "YYYY-MM-DD"),
      new easepick.DateTime(departure, "YYYY-MM-DD"),
    ]);
    this.stays = stays;
    const lockPlugin = this.instance.PluginManager.getInstance("LockPlugin");
    lockPlugin.options.filter = (date, picked) => {
      if (picked.length === 1) {
        const bounds = date.isBefore(picked[0]) ? "[)" : "(]";
        return !picked[0].isSame(date, "day") && date.inArray(stays, bounds);
      }
      return date.inArray(stays, "[)");
    };
    this.instance.renderAll();
  }

  //* Once an arrival is picked, the LockPlugin makes every day that cannot be
  //* the departure ignore clicks (for example the days after the next booked
  //* stay). Marks the ones that are a valid arrival on their own, so a click
  //* on them starts a new selection.
  _markRestartDay(event) {
    const { view, target, date } = event.detail;
    if (view !== "CalendarDay" || this.instance?.datePicked.length !== 1) return;
    if (!target.classList.contains("not-available") || target.classList.contains("locked")) return;

    //* Runs the LockPlugin checks on a detached element as if nothing was picked.
    const picked = this.instance.datePicked.splice(0);
    const probe = document.createElement("div");
    this.instance.PluginManager.getInstance("LockPlugin").onView({ detail: { view, target: probe, date } });
    this.instance.datePicked.push(...picked);

    if (!probe.classList.contains("locked") && !probe.classList.contains("not-available")) {
      target.classList.add("restart");
    }
  }

  //* Marks the first booked night of a stay (stay-start, still free for a
  //* check-out) and the day after the last booked night (stay-end, free for a
  //* check-in), so the stylesheet can shade half of those days. A changeover
  //* day between two stays stays fully booked.
  _markStayEdges(event) {
    const { view, target, date } = event.detail;
    if (view !== "CalendarDay" || !this.stays) return;

    const booked = date.inArray(this.stays, "[)");
    const bookedBefore = date.clone().subtract(1, "day").inArray(this.stays, "[)");
    target.classList.toggle("stay-start", booked && !bookedBefore);
    target.classList.toggle("stay-end", !booked && bookedBefore);
  }

  //* Clears the picked arrival, so the RangePlugin takes the clicked day as the new arrival.
  _restartSelection(event) {
    if (event.target.closest?.(".day.restart")) this.instance.datePicked.length = 0;
  }

  //* Removes the range preview that the RangePlugin draws up to a restart day.
  _hideRestartRange(event) {
    if (!event.target.matches?.(".day.restart")) return;

    this.instance.ui.container.querySelectorAll(".day.in-range, .day.end").forEach((day) => {
      day.classList.remove("in-range", "end", "flipped");
    });
    this.instance.PluginManager.getInstance("RangePlugin").hideTooltip();
  }

  _handleSelect(event) {
    this.log_function("_handleSelect", event);

    const { start, end } = event.detail;
    const startDate = start || false;
    const endDate = end || false;
    this.startsTarget.value = startDate ? moment(startDate).format("DD/MM/YYYY") : "";
    this.endsTarget.value = endDate ? moment(endDate).format("DD/MM/YYYY") : "";
    this.durationTarget.value = startDate && endDate ? moment(endDate).diff(moment(startDate), "days") : "";
    const custom_event = new CustomEvent("easepick-handle-select", event);
    window.dispatchEvent(custom_event);
  }

  shake() {
    this.log_function("shake");

    if (!this.shaking) {
      this.calendarContainerTarget.classList.add("animate__animated", "animate__shakeX");
      this.shaking = true;

      setTimeout(() => {
        this.calendarContainerTarget.classList.remove("animate__animated", "animate__shakeX");
        this.shaking = false;
      }, 1000);
    }
  }

  disconnect() {
    this.log_function("disconnect");

    this.instance.destroy();
    this.instance = null;
  }

  get currentLocale() {
    this.log_function("get currentLocale");

    switch (this.localeValue) {
      case "en":
        return "en-US";
      case "el":
        return "el-GR";
      default:
        return "en-US";
    }
  }

  get nightsText() {
    this.log_function("get nightsText");

    switch (this.localeValue) {
      case "en":
        return { one: "night", other: "nights" };
      case "el":
        return { one: "διανυκτέρευση", other: "διανυκτερεύσεις" };
      default:
        return { one: "night", other: "nights" };
    }
  }

}
